import { buildGuessDisplay, type NoteState } from "../model/NoteState";
import type { LevelState } from "../model/LevelState";
import type { DebugSaveState, DebugSnapshot } from "../types/debug";
import type { GameSave } from "../types/save";

function cloneCollectiblesByLevel(
  byLevel: Readonly<Record<string, readonly string[]>>,
): Readonly<Record<string, readonly string[]>> {
  const keys = Object.keys(byLevel).sort((left, right) => left.localeCompare(right));
  const cloned: Record<string, readonly string[]> = {};

  for (const key of keys) {
    cloned[key] = [...(byLevel[key] ?? [])];
  }

  return cloned;
}

function buildSaveStateSurface(save: GameSave): DebugSaveState {
  return {
    clearedLevels: [...save.progression.clearedLevels],
    perfectedLevels: [...save.progression.perfectedLevels],
    unlockedLevels: [...save.progression.unlockedLevels],
    unlockedChapters: [...save.progression.unlockedChapters],
    passedLocks: [...save.progression.passedLocks],
    collectiblesByLevel: cloneCollectiblesByLevel(save.collectibles.byLevel),
    permanentRevealLevels: [...save.fog.permanentRevealLevels],
  };
}

function createBaseSnapshot(levelState: LevelState): Pick<
  DebugSnapshot,
  "input" | "result" | "reason"
> {
  return {
    input: levelState.lastDebugSnapshot?.input ?? { raw: "startup", mapped: null },
    result: levelState.lastDebugSnapshot?.result ?? "valid",
    reason: levelState.lastDebugSnapshot?.reason ?? null,
  };
}

export function buildDebugSurface(
  sceneName: string,
  levelId: string | null,
  levelState: LevelState,
  noteState: NoteState,
  debugEnabled: boolean,
  save: GameSave,
): DebugSnapshot {
  const baseSnapshot = createBaseSnapshot(levelState);

  return {
    tick: levelState.tick,
    input: baseSnapshot.input,
    result: baseSnapshot.result,
    reason: baseSnapshot.reason,
    sceneName,
    levelId,
    playerPosition: {
      x: levelState.player.x,
      y: levelState.player.y,
      facing: levelState.player.facing,
    },
    truthLine: levelState.symbolSequence.expanded.join(" "),
    guessLine: buildGuessDisplay(noteState, levelState.symbolSequence.raw),
    completionState: {
      cleared: levelState.completion.cleared,
      perfected: levelState.completion.perfected,
    },
    fogPermanentlyRevealed: levelState.fog.permanentReveal,
    debugFogReveal: levelState.fog.debugReveal,
    debugEnabled,
    saveState: buildSaveStateSurface(save),
    state: {
      player: {
        x: levelState.player.x,
        y: levelState.player.y,
        facing: levelState.player.facing,
      },
      history: {
        length: levelState.history.length,
        last5: [...levelState.history.last5],
      },
      fog: {
        explored: levelState.fog.explored.map((point) => ({ x: point.x, y: point.y })),
        permanentlyRevealed: levelState.fog.permanentReveal,
        debugReveal: levelState.fog.debugReveal,
      },
      collectibles: {
        local: [...levelState.collectibles.local],
        permanent: [...levelState.collectibles.permanent],
      },
      breadcrumbs: {
        active: levelState.breadcrumbs.active.map((b) => ({ ...b })),
        local: [...levelState.breadcrumbs.local],
      },
      symbolSequence: {
        raw: [...levelState.symbolSequence.raw],
        expanded: [...levelState.symbolSequence.expanded],
        playerNotes: { ...levelState.symbolSequence.playerNotes },
      },
    },
  };
}

export class DebugController {
  private _debugEnabled: boolean = false;

  public get debugEnabled(): boolean {
    return this._debugEnabled;
  }

  public toggleDebug(): boolean {
    this._debugEnabled = !this._debugEnabled;
    return this._debugEnabled;
  }

  public buildSurface(
    sceneName: string,
    levelId: string | null,
    levelState: LevelState,
    noteState: NoteState,
    save: GameSave,
  ): DebugSnapshot {
    return buildDebugSurface(sceneName, levelId, levelState, noteState, this._debugEnabled, save);
  }
}
